import { SearchInput } from './SearchInput';
import { MultiSelect } from './MultiSelect';
import { ProgramsDropdown } from './ProgramsDropdown';
import { TransactionStatus, TransactionType, TransactionCategory, formatTransactionCategory, formatTransactionStatus, formatTransactionType } from '../constants/transaction';
import type { TransactionFilterValues } from '../hooks/useTransactionFilters';

interface Props {
  filters: TransactionFilterValues;
  onChange: (name: keyof TransactionFilterValues, value: string | string[]) => void;
  onClear: () => void;
  canClear: boolean;
}

const statusOptions = Object.values(TransactionStatus).map((s) => ({ value: s, label: formatTransactionStatus(s) }));
const categoryOptions = Object.values(TransactionCategory).map((c) => ({ value: c, label: formatTransactionCategory(c) }));

const fieldClass = 'px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-900';
const labelClass = 'text-xs font-medium text-gray-500';

export const TransactionFilters = ({ filters, onChange, onClear, canClear }: Props) => {
  const isRangeInvalid = filters.from !== '' && filters.to !== '' && filters.to < filters.from;
  const isRangePartial = (filters.from === '') !== (filters.to === '');

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <span className={labelClass}>Search</span>
          <SearchInput value={filters.q} onChange={(v) => onChange('q', v)} placeholder="Name or program" label="Search transactions" />
        </div>
        <div className="flex flex-col gap-1 w-full sm:w-40">
          <span className={labelClass}>Status</span>
          <MultiSelect options={statusOptions} values={filters.statuses} onChange={(v) => onChange('statuses', v)} placeholder="All statuses" label="Status" />
        </div>
        <div className="flex flex-col gap-1 w-full sm:w-40">
          <span className={labelClass}>Category</span>
          <MultiSelect options={categoryOptions} values={filters.categories} onChange={(v) => onChange('categories', v)} placeholder="All categories" label="Category" />
        </div>
        <label className="flex flex-col gap-1 w-full sm:w-32">
          <span className={labelClass}>Type</span>
          <select value={filters.type} onChange={(e) => onChange('type', e.target.value)} className={fieldClass}>
            <option value="">All types</option>
            {Object.values(TransactionType).map((t) => (
              <option key={t} value={t}>{formatTransactionType(t)}</option>
            ))}
          </select>
        </label>
        <div className="flex flex-col gap-1 w-full sm:w-56">
          <span className={labelClass}>Program</span>
          <ProgramsDropdown value={filters.program_uid} onChange={(v) => onChange('program_uid', v)} />
        </div>
        <label className="flex flex-col gap-1">
          <span className={labelClass}>From</span>
          <input type="date" value={filters.from} max={filters.to || undefined} onChange={(e) => onChange('from', e.target.value)} className={fieldClass} />
        </label>
        <label className="flex flex-col gap-1">
          <span className={labelClass}>To</span>
          <input type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => onChange('to', e.target.value)} className={fieldClass} />
        </label>
        {canClear && (
          <button
            type="button"
            onClick={onClear}
            className="py-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>
      {isRangeInvalid && <span className="text-xs text-red-500">"To" date must be on or after the "From" date.</span>}
      {isRangePartial && <span className="text-xs text-gray-500">Date filter not applied yet. Pick both a "From" and "To" date to filter by date.</span>}
    </div>
  );
};
